import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import * as XLSX from "xlsx"
import { useSelector } from "react-redux"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import { TableData } from "../../../core/data/interface"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import {
  Book,
  deleteBook,
  getAllBooks,
  getBookById,
  getBookItemByBarcode,
  PaginationData,
} from "../../../service/books"
import { all_routes } from "../../router/all_routes"
import LibraryModal from "./libraryModal"
import ReturnBookModal from "./ReturnBookModal"
// Sort options for books
const BOOKS_SORT_OPTIONS = [
  {
    label: "Newest First",
    value: {
      sortBy: "createdAt",
      sortOrder: "desc",
    },
  },
  {
    label: "Oldest First",
    value: {
      sortBy: "createdAt",
      sortOrder: "asc",
    },
  },
  {
    label: "Name (A->Z)",
    value: {
      sortBy: "title",
      sortOrder: "asc",
    },
  },
  {
    label: "Name (Z->A)",
    value: {
      sortBy: "title",
      sortOrder: "desc",
    },
  },
]

const Books = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [books, setBooks] = useState<Book[]>([])
  const [pagination, setPagination] = useState({ limit: 10, page: 1 })
  const [paginationData, setPaginationData] = useState<PaginationData>()
  const [searchText, setSearchText] = useState("")
  const [selectedBook, setSelectedBook] = useState<Book>()
  const [deleteBookId, setDeleteBookId] = useState<string | null>(null)

  const [appliedFilters, setAppliedFilters] = useState<{
    author?: string
    sortBy?: string
    sortOrder?: string
  } | null>(null)

  const [sortBy, setSortBy] = useState(BOOKS_SORT_OPTIONS[0])

  // Barcode scanner state
  const [barcodeInput, setBarcodeInput] = useState("")
  const [barcodeLoading, setBarcodeLoading] = useState(false)
  const [barcodeError, setBarcodeError] = useState("")
  const [barcodeResult, setBarcodeResult] = useState<{ item: any; book: any; issue?: any } | null>(null)
  const [returnIssue, setReturnIssue] = useState<any | null>(null)
  const [showCameraModal, setShowCameraModal] = useState(false)
  const html5QrcodeRef = useRef<any>(null)

  const safeStopScanner = (s: any) => {
    if (!s) return
    try {
      const p = s.stop()
      if (p && typeof p.catch === "function") p.catch(() => {})
    } catch {}
  }

  const stopCamera = () => setShowCameraModal(false)

  const searchByBarcode = async (val: string) => {
    const code = val.trim()
    if (!code) return
    setBarcodeLoading(true)
    setBarcodeError("")
    setBarcodeResult(null)
    try {
      const res = await getBookItemByBarcode(code)
      if (res.success && res.data) setBarcodeResult(res.data)
      else setBarcodeError("No copy found for this barcode.")
    } catch (err: any) {
      setBarcodeError(err?.message || "Barcode not found.")
    } finally {
      setBarcodeLoading(false)
    }
  }

  useEffect(() => {
    if (!showCameraModal) return
    let scanner: any = null
    const init = async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode")
        scanner = new Html5Qrcode("books-barcode-scanner")
        html5QrcodeRef.current = scanner
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (w: number, h: number) => ({
              width: Math.min(300, Math.round(w * 0.85)),
              height: Math.min(120, Math.round(h * 0.4)),
            }),
          },
          async (val: string) => {
            safeStopScanner(scanner)
            html5QrcodeRef.current = null
            setBarcodeInput(val)
            setShowCameraModal(false)
            setBarcodeLoading(true)
            setBarcodeError("")
            setBarcodeResult(null)
            try {
              const res = await getBookItemByBarcode(val)
              if (res.success && res.data) setBarcodeResult(res.data)
              else setBarcodeError("No copy found for this barcode.")
            } catch (err: any) {
              setBarcodeError(err?.message || "Barcode not found.")
            } finally {
              setBarcodeLoading(false)
            }
          },
          () => {},
        )
      } catch (err: any) {
        toast.error(err?.message || "Camera access denied. Please allow camera permission.")
        setShowCameraModal(false)
      }
    }
    init()
    return () => {
      safeStopScanner(scanner)
      html5QrcodeRef.current = null
    }
  }, [showCameraModal])

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
    setAppliedFilters(prev => ({
      ...prev,
      ...item.value,
    }))
  }

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Books List",
  })

  const fetchBooks = async () => {
    setLoading(true)
    try {
      const response = await getAllBooks({
        ...pagination,
        ...appliedFilters,
        ...sortBy.value,
        searchText,
      })

      if (response.success) {
        setBooks(response.data)
        setPaginationData({
          currentPage: response.pagination.currentPage,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          limit: response.pagination.limit,
          hasNextPage:
            response.pagination.currentPage < response.pagination.totalPages,
          hasPrevPage: response.pagination.currentPage > 1,
        })
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setBooks([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBooks()
  }, [pagination, appliedFilters, sortBy, searchText])

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }
    fetchBooks()
  }

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page })
  }

  const handleResetFilters = () => {
    setAppliedFilters(null)
    setSearchText("")
    setSortBy(BOOKS_SORT_OPTIONS[0])
  }

  // Handle book actions
  const handleEditBook = async (book: Book) => {
    try {
      setSelectedBook(book)

      getBookById(book._id)
        .then(response => {
          if (response.success) {
            setSelectedBook(response.data)
          }
        })
        .catch(error => {
          console.error("Error fetching book details:", error)
        })
    } catch (error) {
      console.error("Error in handleEditBook:", error)
    }
  }

  // Delete book function
  const handleDeleteBook = async () => {
    if (!deleteBookId) return

    try {
      setLoading(true)

      // Optimistic update
      setBooks(prevBooks => prevBooks.filter(book => book._id !== deleteBookId))

      const response = await deleteBook(deleteBookId)
      if (response.success) {
        toast.success("Book deleted successfully!")
      } else {
        fetchBooks()
        toast.error("Failed to delete book")
      }
    } catch (error) {
      fetchBooks()
      console.error("Error deleting book:", error)
      toast.error("Error deleting book. Please try again.")
    } finally {
      setLoading(false)
      setDeleteBookId(null)
    }
  }

  // Open delete confirmation modal
  const openDeleteModal = (book: Book) => {
    setDeleteBookId(book._id)
  }

  const handleAddNewBook = () => {
    setSelectedBook(undefined)
  }

  const handleBookAdded = () => {
    fetchBooks()
    setSelectedBook(undefined)
  }

  const handleBookUpdated = () => {
    fetchBooks()
    setSelectedBook(undefined)
  }

  const handleExportExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(
        books.map(item => ({
          ID: item.bookIdentifier,
          "Book Name": item.title,
          ISBN: item.isbn,
          Publisher: item.publisher,
          Author: item.author,
          Classification: item.classificationCode,
          "Cabinet No": item.cabinetNo,
          "Shelf No": item.shelfNo,
          Quantity: item.quantity,
          Available: item.available_quantity,
          "Post Date": item.createdAt,
          Price: item.price,
        })),
      )

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Books")
      XLSX.writeFile(wb, "books.xlsx")
    } catch (error) {
      console.error("Error exporting to Excel:", error)
      toast.error("Error exporting to Excel")
    }
  }

  const handleExportPdf = () => {
    handlePrint()
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "bookIdentifier",
      render: (text: string, record: TableData) => (
        <Link
          to={routes.libraryBookItems.replace(":id", String(record._id))}
          className="link-primary"
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Book Name",
      dataIndex: "title",
    },
    {
      title: "ISBN",
      dataIndex: "isbn",
    },
    {
      title: "Publisher",
      dataIndex: "publisher",
    },
    {
      title: "Author",
      dataIndex: "author",
    },
    {
      title: "Classification",
      dataIndex: "classificationCode",
    },
    {
      title: "Cabinet",
      dataIndex: "cabinetNo",
      render: (_: any, record: TableData) => (record as any).cabinetNo || "—",
    },
    {
      title: "Shelf",
      dataIndex: "shelfNo",
      render: (_: any, record: TableData) => (record as any).shelfNo || "—",
    },
    {
      title: "Total",
      dataIndex: "quantity",
    },
    {
      title: "Available",
      dataIndex: "available_quantity",
    },
    {
      title: "Post Date",
      dataIndex: "createdAt",
      render: (t: string) => (t ? new Date(t).toLocaleString() : "—"),
    },
    {
      title: "Price",
      dataIndex: "price",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Book) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_library_book"
                    onClick={() => handleEditBook(record as Book)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#delete_library_book"
                    onClick={() => openDeleteModal(record as Book)}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Books</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Books
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchBooks}
                onExportPdf={handleExportPdf} // Print function
                onExportExcel={handleExportExcel} // Excel export function
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_library_book"
                  onClick={handleAddNewBook}
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Book
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Barcode Scanner */}
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="mb-3">
                <i className="ti ti-barcode me-2 text-primary" />
                Search by Barcode
              </h5>
              <div className="d-flex gap-2 flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  style={{ maxWidth: 320 }}
                  placeholder="Scan or enter barcode..."
                  value={barcodeInput}
                  onChange={e => setBarcodeInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && searchByBarcode(barcodeInput)}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => searchByBarcode(barcodeInput)}
                  disabled={barcodeLoading}
                >
                  {barcodeLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <><i className="ti ti-search me-1" />Search</>
                  )}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCameraModal(true)}
                >
                  <i className="ti ti-scan me-1" />Scan Barcode
                </button>
                {(barcodeResult || barcodeError) && (
                  <button
                    className="btn btn-light"
                    onClick={() => { setBarcodeInput(""); setBarcodeResult(null); setBarcodeError("") }}
                  >
                    <i className="ti ti-x me-1" />Clear
                  </button>
                )}
              </div>

              {barcodeError && (
                <div className="alert alert-danger mt-3 mb-0 py-2">{barcodeError}</div>
              )}

              {barcodeResult && (
                <div className="border rounded-3 p-4 mt-3 bg-light" style={{ fontSize: 15 }}>
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">
                        <i className="ti ti-book me-2 text-primary" />
                        {barcodeResult.book.title}
                      </h5>
                      <div className="text-muted" style={{ fontSize: 14 }}>by {barcodeResult.book.author || "—"}</div>
                    </div>
                    <span className={`badge px-3 py-2 ${barcodeResult.item.status === "available" ? "bg-success" : barcodeResult.item.status === "issued" ? "bg-info" : barcodeResult.item.status === "lost" ? "bg-danger" : "bg-warning"} text-capitalize`} style={{ fontSize: 14 }}>
                      {barcodeResult.item.status}
                    </span>
                  </div>
                  <div className="row g-4">
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Publisher</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book.publisher || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>ISBN</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book.isbn || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>DDC Class</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book.classificationCode || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Copy ID</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item.copyIdentifier || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Condition</div>
                      <div className="fw-bold text-capitalize" style={{ fontSize: 15 }}>{barcodeResult.item.condition || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Cabinet</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item.cabinetNo || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Shelf</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item.shelfNo || "—"}</div>
                    </div>
                    {barcodeResult.issue && (
                      <div className="col-12">
                        <div className="text-muted mb-1" style={{ fontSize: 13 }}>Issued To</div>
                        <div className="fw-bold" style={{ fontSize: 15 }}>
                          {barcodeResult.issue.student?.name || "—"}
                          {barcodeResult.issue.student?.admission_no && (
                            <span className="text-muted ms-2" style={{ fontSize: 13 }}>({barcodeResult.issue.student.admission_no})</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 d-flex gap-2 flex-wrap">
                    <Link
                      to={routes.libraryBookItems.replace(":id", String(barcodeResult.book._id))}
                      className="btn btn-outline-primary"
                    >
                      <i className="ti ti-books me-1" />View All Copies
                    </Link>
                    {barcodeResult.issue && (
                      <button
                        className="btn btn-warning"
                        onClick={() => setReturnIssue(barcodeResult.issue)}
                      >
                        <i className="ti ti-arrow-back-up me-2" />Return This Copy
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* /Barcode Scanner */}

          {/* Books List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Books</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Author</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by author name"
                                onChange={e =>
                                  setAppliedFilters(prev => ({
                                    ...prev,
                                    author: e.target.value || undefined,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {BOOKS_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Printable content - Classes component کی طرح */}
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Books List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {/* Books List Table */}
              <Table
                dataSource={books}
                columns={columns}
                pagination={{
                  ...paginationData,
                  onChange: handleChange,
                }}
                loading={loading}
                setSearchText={setSearchText}
              />
              {/* /Books List Table */}
            </div>
          </div>
          {/* /Books List */}
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Library Modal */}
      <LibraryModal
        selectedItem={selectedBook}
        onItemAdded={handleBookAdded}
        onItemUpdated={handleBookUpdated}
      />

      {/* Camera Barcode Scanner Modal */}
      {showCameraModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.6)", zIndex: 1060 }}
          onClick={e => { if (e.target === e.currentTarget) stopCamera() }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ti ti-scan me-2 text-primary" />
                  Scan Barcode
                </h5>
                <button type="button" className="btn-close" onClick={stopCamera} />
              </div>
              <div className="modal-body p-0">
                <div id="books-barcode-scanner" style={{ width: "100%" }} />
              </div>
              <div className="modal-footer justify-content-center">
                <small className="text-muted">Point camera at the book barcode</small>
              </div>
            </div>
          </div>
        </div>
      )}

      <ReturnBookModal
        issue={returnIssue}
        onClose={() => setReturnIssue(null)}
        onSuccess={() => { setBarcodeResult(null); setBarcodeInput("") }}
      />

      {/* Delete Confirmation Modal */}
      <div className="modal fade" id="delete_library_book">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-body text-center p-5">
              <div
                className="d-flex justify-content-center align-items-center bg-danger bg-opacity-10 rounded-circle mx-auto mb-3"
                style={{ width: "90px", height: "90px" }}
              >
                <i
                  className="ti ti-trash-x text-danger"
                  style={{ fontSize: "48px" }}
                ></i>
              </div>

              <h3 className="fw-bold mb-2">Delete Book</h3>
              <p className="text-muted mb-4 px-3">
                Are you sure you want to delete this book? This action cannot be
                undone.
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  className="btn btn-light px-4"
                  data-bs-dismiss="modal"
                  onClick={() => setDeleteBookId(null)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-danger px-4"
                  onClick={handleDeleteBook}
                  disabled={loading}
                  data-bs-dismiss="modal"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Books
